import { Pencil, Search, Trash2, Users } from "lucide-react";
import { useMemo, useState, type JSX } from "react";

import type { GroupDTO, MemberDTO } from "../../common/api/types.js";
import { PrimaryButton } from "../../common/components/PrimaryButton.js";
import { useAdminGroups, useAdminMembers, useCreateGroup, useDeleteGroup, useUpdateGroup } from "../adminQueries.js";

/** Nur aktive Mitglieder können einer Gruppe angehören; Eingeladene folgen nach der Annahme. */
function useActiveMembers(): MemberDTO[] {
  const members = useAdminMembers();
  return useMemo(() => (members.data ?? []).filter((member) => member.status === "ACTIVE"), [members.data]);
}

function MemberPicker({ members, selected, onChange }: { members: MemberDTO[]; selected: string[]; onChange: (ids: string[]) => void }): JSX.Element {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const visible = needle ? members.filter((member) => `${member.displayName} ${member.email}`.toLowerCase().includes(needle)) : members;
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((entry) => entry !== id) : [...selected, id]);
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-ink-muted">Mitglieder auswählen · {selected.length} gewählt</span>
        {members.length > 8 ? (
          <label className="flex min-h-8 items-center gap-1 rounded-lg border border-line bg-surface px-2">
            <Search className="h-3.5 w-3.5 text-ink-muted" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Person suchen" aria-label="Person suchen" className="w-32 bg-transparent text-xs outline-none" />
          </label>
        ) : null}
      </div>
      <div className="mt-2 flex max-h-48 flex-wrap gap-2 overflow-y-auto">
        {visible.map((member) => (
          <button
            key={member.userId}
            type="button"
            aria-pressed={selected.includes(member.userId)}
            onClick={() => toggle(member.userId)}
            className={selected.includes(member.userId) ? "rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-brand-ink" : "rounded-full bg-surface-sunken px-3 py-1.5 text-xs font-semibold text-ink-muted hover:text-ink"}
          >
            {member.displayName}
          </button>
        ))}
        {visible.length === 0 ? <p className="text-xs text-ink-muted">Keine aktiven Personen gefunden.</p> : null}
      </div>
    </div>
  );
}

function GroupCard({ group, members }: { group: GroupDTO; members: MemberDTO[] }): JSX.Element {
  const update = useUpdateGroup();
  const remove = useDeleteGroup();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [selected, setSelected] = useState<string[]>(group.memberIds);
  const byId = new Map(members.map((member) => [member.userId, member]));
  const startEditing = () => { setName(group.name); setSelected(group.memberIds); update.reset(); setEditing(true); };
  const save = () => update.mutate({ groupId: group.id, body: { name, memberIds: selected } }, { onSuccess: () => setEditing(false) });
  const confirmDelete = () => { if (window.confirm(`Gruppe „${group.name}“ wirklich löschen?`)) remove.mutate(group.id); };

  return (
    <section className="rounded-xl border border-line bg-surface-raised p-4">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 shrink-0 text-brand" />
        {editing ? (
          <input value={name} onChange={(event) => setName(event.target.value)} aria-label="Gruppenname" className="min-h-9 min-w-0 flex-1 rounded-lg border border-line px-2 font-bold" />
        ) : (
          <h3 className="min-w-0 flex-1 truncate font-bold text-ink">{group.name}</h3>
        )}
        {editing ? null : (
          <>
            <button type="button" onClick={startEditing} title="Gruppe bearbeiten" className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"><Pencil className="h-4 w-4" /></button>
            <button type="button" onClick={confirmDelete} disabled={remove.isPending} title="Gruppe löschen" className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-danger"><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
      {editing ? (
        <>
          <MemberPicker members={members} selected={selected} onChange={setSelected} />
          {update.error ? <p className="mt-2 text-sm text-danger">{update.error.message}</p> : null}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} className="rounded-lg border border-line px-3 py-2 text-sm font-semibold text-ink hover:bg-surface-sunken">Abbrechen</button>
            <PrimaryButton disabled={!name.trim() || update.isPending} onClick={save}>Speichern</PrimaryButton>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-ink-muted">{group.memberIds.length === 1 ? "1 Mitglied" : `${group.memberIds.length} Mitglieder`}</p>
          {group.memberIds.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {group.memberIds.map((id) => <li key={id} className="rounded-full bg-surface-sunken px-2.5 py-1 text-xs text-ink">{byId.get(id)?.displayName ?? "Unbekannte Person"}</li>)}
            </ul>
          ) : (
            <button type="button" onClick={startEditing} className="mt-2 text-sm font-semibold text-brand hover:underline">Personen hinzufügen</button>
          )}
          {remove.error ? <p className="mt-2 text-sm text-danger">{remove.error.message}</p> : null}
        </>
      )}
    </section>
  );
}

export function Groups(): JSX.Element {
  const groups = useAdminGroups();
  const members = useActiveMembers();
  const create = useCreateGroup();
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const submit = () => create.mutate({ name, memberIds: selected }, { onSuccess: () => { setName(""); setSelected([]); } });

  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-surface-sunken p-4 sm:p-6">
      <section className="rounded-xl border border-line bg-surface-raised p-4">
        <h3 className="font-bold text-ink">Neue Gruppe</h3>
        <div className="mt-3 flex gap-2">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Gruppenname" aria-label="Gruppenname" className="min-h-10 min-w-0 flex-1 rounded-lg border border-line px-3" />
          <PrimaryButton disabled={!name.trim() || create.isPending} onClick={submit}>Anlegen</PrimaryButton>
        </div>
        <MemberPicker members={members} selected={selected} onChange={setSelected} />
        {create.error ? <p className="mt-2 text-sm text-danger">{create.error.message}</p> : null}
      </section>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {(groups.data ?? []).map((group) => <GroupCard key={group.id} group={group} members={members} />)}
      </div>
    </div>
  );
}
